import * as Sentry from '@sentry/react'
import {
  configureStore,
  combineReducers
} from '@reduxjs/toolkit'
import {
  persistReducer,
  persistStore
} from 'redux-persist'
import thunk from 'redux-thunk'
import storage from 'redux-persist/lib/storage'
import snackbarReducer, { snackbarSlice } from '../features/snackbar/snackbarSlice'
import dataReducer, {
  dataSlice,
  DataState
} from '../features/data/dataSlice'
import networkReducer, { networkSlice } from '../features/network/networkSlice'
import sessionReducer, { sessionSlice, SessionState } from '../features/session/sessionSlice'
import themeReducer, { themeSlice } from '../features/theme/themeSlice'
import cdnApi from '../api/cdnApi'
import ctliveApi from '../api/ctliveApi'
import { createLogger } from './logger'
import checkinReducer, { checkinSlice } from '../features/checkin/checkinSlice'
import filterReducer, { filterSlice } from '../features/filter/filterSlice'
import cameraReducer, { cameraSlice } from '../features/camera/cameraSlice'
import sseReducer, { sseSlice } from '../features/sse/sseSlice'
import serviceWorkerReducer, { serviceWorkerSlice } from '../features/serviceWorker/serviceWorkerSlice'

const logger = createLogger({
  prefix: 'ReduxStore'
})

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer: (action: any) => {
    // remove the following keys from the action
    if (['cdnApi', 'ctliveApi'].find(prefix => action.type.startsWith(prefix))) {
      return null
    }
    if (action.type === 'data/updateS3Entries') {
      return {
        ...action,
        payload: Array.isArray(action.payload)
          ? action.payload.length
          : Object.keys(action.payload || {})
      }
    }
    return action
  },
  // Optionally pass options listed below
  // eslint-disable-next-line no-use-before-define
  stateTransformer: (state: RootState) => {
    // remove the following keys from the state
    const {
      camera,
      checkin,
      data,
      filter,
      network,
      session,
      sse,
      serviceWorker,
    } = state

    return {
      camera,
      checkin,
      data: {
        ...data,
        s3Entries: null, // could be huge, do not send
      },
      filter,
      network,
      session,
      sse,
      serviceWorker,
    }
  }
})

export const makeStore = (preloadedState?: any) => {
  const rootReducer = persistReducer({
    key: 'ChronoCheckin',
    storage,
    whitelist: [sessionSlice.name],
    version: 21,
    debug: process.env.NODE_ENV !== 'production',
    // TODO this is done as a bandaid - people testing with stored Redux state will have a null pointer error
    migrate: async (currentState, currentVersion) => {
      if (!currentState) {
        return
      }
      const data = (currentState as any).data ?? {}
      const { name, username, uuid, kioskId, ...sessionState } = (currentState as any).session ?? {}
      const newState = {
        ...currentState,
        data: {
          ...data,
          entries: data.entries ?? [],
          requiresRefetch: false, // always reset this - can not refetch on initial load
          s3Entries: data.s3Entries ?? [],
          updatesOurs: data.unsyncedRequests ?? [],
          updatesTheirs: data.updatesTheirs ?? [],
        } as DataState,
        session: {
          ...sessionState,
          username: username || name || undefined,
          kioskId: kioskId || uuid || undefined,
        } as SessionState,
      }
      logger.info(`Migrating Redux v${currentVersion}:`, {
        prevState: currentState,
        newState,
      })

      return newState
    }
  }, combineReducers({
    [dataSlice.name]: dataReducer,
    [filterSlice.name]: filterReducer,
    [themeSlice.name]: themeReducer,
    [networkSlice.name]: networkReducer,
    [sessionSlice.name]: sessionReducer,
    [snackbarSlice.name]: snackbarReducer,
    [sseSlice.name]: sseReducer,
    [ctliveApi.reducerPath]: ctliveApi.reducer,
    [cdnApi.reducerPath]: cdnApi.reducer,
    [checkinSlice.name]: checkinReducer,
    [cameraSlice.name]: cameraReducer,
    [serviceWorkerSlice.name]: serviceWorkerReducer,
  }))
  return configureStore({
    devTools: process.env.NODE_ENV !== 'production',
    reducer: rootReducer,
    preloadedState,
    middleware: [
      thunk,
      ctliveApi.middleware,
      cdnApi.middleware,
    ],
    enhancers: enhancers => [
      ...enhancers,
      sentryReduxEnhancer
    ],
  })
}

export const store = makeStore()

export const persistor = persistStore(store)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
